import {
  buildChecklistResponseInitialValues,
  buildPreStartChecklistSavePayload,
  hasBlockingPreStartUploads,
  hasPreStartChecklistSaveChanges,
  serializePreStartChecklistResponses
} from "../../JobShiftPreStart/jobShiftPreStartFormUtils";

export const READINESS_QUESTIONS_FORM_NAME = "job_shift_rescue_readiness_questions_form";
export const READINESS_HEADING_KEY = "readiness_checklist_heading";

export const getReadinessChecklist = (readiness = {}) => (
  readiness && readiness.rescue_readiness_questions
    ? readiness.rescue_readiness_questions
    : {}
);

const getReadinessChecklistForForm = (readiness = {}) => {
  const checklist = getReadinessChecklist(readiness);
  const headingContents = checklist.heading_contents && typeof checklist.heading_contents === "object"
    ? checklist.heading_contents
    : {};
  const snapshotQuestions = Array.isArray(checklist.snapshot_questions)
    ? checklist.snapshot_questions
    : (Array.isArray(checklist.questions) ? checklist.questions : []);

  const getHeading = (key) => typeof headingContents[key] === "string" ? headingContents[key] : "";

  return {
    ...checklist,
    snapshot_questions: snapshotQuestions,
    prestart_checklist_heading: getHeading("rescue_readiness_checklist_heading"),
    prestart_staff_signoff: getHeading("rescue_readiness_staff_signoff")
  };
};

export const buildReadinessQuestionsInitialValues = (readiness = {}) => {
  const checklist = getReadinessChecklist(readiness);
  const headingContents = checklist.heading_contents && typeof checklist.heading_contents === "object"
    ? checklist.heading_contents
    : {};
  const getHeading = (key) => {
    return typeof headingContents[key] === "string" ? headingContents[key] : "";
  };
  const preStartInitialValues = buildChecklistResponseInitialValues(getReadinessChecklistForForm({
    ...readiness,
    rescue_readiness_questions: {
      ...checklist,
      prestart_checklist_heading: getHeading("rescue_readiness_checklist_heading"),
      prestart_staff_signoff: getHeading("rescue_readiness_staff_signoff")
    }
  }));

  const responseValues = { ...preStartInitialValues };
  const readinessHeading = responseValues.prestart_checklist_heading;
  const readinessSignoff = responseValues.prestart_staff_signoff;
  delete responseValues.prestart_checklist_heading;
  delete responseValues.prestart_staff_signoff;

  return {
    ...responseValues,
    readiness_checklist_heading: readinessHeading,
    readiness_staff_signoff: readinessSignoff
  };
};



// The answer contract is shared with Pre-Start, but the Readiness resource is
// deliberately kept at its own boundary so the nested response is never lost.
export const buildReadinessSavePayload = (values = {}, readiness = {}) => {
  const checklist = getReadinessChecklistForForm(readiness);
  const payload = buildPreStartChecklistSavePayload({
    ...values,
    prestart_checklist_heading: values.readiness_checklist_heading,
    prestart_staff_signoff: values.readiness_staff_signoff
  }, checklist);

  if (Object.prototype.hasOwnProperty.call(payload, "prestart_checklist_heading")) {
    payload.readiness_checklist_heading = payload.prestart_checklist_heading;
    delete payload.prestart_checklist_heading;
  }
  if (Object.prototype.hasOwnProperty.call(payload, "prestart_staff_signoff")) {
    payload.readiness_staff_signoff = payload.prestart_staff_signoff;
    delete payload.prestart_staff_signoff;
  }
  const currentHeadings = checklist.heading_contents && typeof checklist.heading_contents === "object"
    ? { ...checklist.heading_contents }
    : {};
  let headingChanged = false;
  const upsertHeading = (key, value) => {
    if (value !== undefined) currentHeadings[key] = value || "";
  };
  if (Object.prototype.hasOwnProperty.call(payload, "readiness_checklist_heading")) {
    upsertHeading("rescue_readiness_checklist_heading", payload.readiness_checklist_heading);
    delete payload.readiness_checklist_heading;
    headingChanged = true;
  }
  if (Object.prototype.hasOwnProperty.call(payload, "readiness_staff_signoff")) {
    upsertHeading("rescue_readiness_staff_signoff", payload.readiness_staff_signoff);
    delete payload.readiness_staff_signoff;
    headingChanged = true;
  }
  if (headingChanged) {
    payload.heading_contents = currentHeadings;
  }
  return payload;
};

export const hasReadinessSaveChanges = (payload = {}) => {
  return Boolean(
    (Array.isArray(payload.answer_updates) && payload.answer_updates.length > 0)
    || Boolean(payload.heading_contents && typeof payload.heading_contents === "object")
    || Object.prototype.hasOwnProperty.call(payload, "readiness_checklist_heading")
    || Object.prototype.hasOwnProperty.call(payload, "readiness_staff_signoff")
  );
};

export const hasBlockingReadinessUploads = (values = {}, readiness = {}) => (
  hasBlockingPreStartUploads(values, getReadinessChecklist(readiness))
);

export const serializeReadinessResponses = (values = {}, readiness = {}) => {
  const checklist = getReadinessChecklistForForm(readiness);
  const serializedChecklist = serializePreStartChecklistResponses({
    ...values,
    prestart_checklist_heading: values.readiness_checklist_heading,
    prestart_staff_signoff: values.readiness_staff_signoff
  }, checklist);
  const serialized = { ...serializedChecklist };
  delete serialized.prestart_checklist_heading;
  delete serialized.prestart_staff_signoff;

  const existingHeadingContents = checklist.heading_contents && typeof checklist.heading_contents === "object"
    ? checklist.heading_contents
    : {};
  const headingContents = {
    ...existingHeadingContents,
    rescue_readiness_checklist_heading: values.readiness_checklist_heading !== undefined
      ? values.readiness_checklist_heading || ""
      : (existingHeadingContents.rescue_readiness_checklist_heading || ""),
    rescue_readiness_staff_signoff: values.readiness_staff_signoff !== undefined
      ? values.readiness_staff_signoff || ""
      : (existingHeadingContents.rescue_readiness_staff_signoff || "")
  };

  return {
    ...readiness,
    rescue_readiness_questions: {
      ...serialized,
      heading_contents: headingContents
    }
  };
};
